import { Typography } from '@euler/app/components/typography';
import { Center } from '@euler/components/Center';
import { Icon } from '@euler/components/Icomoon';
import { HStack } from '@euler/components/layout/Stack';
import { NumberInput } from '@euler/components/NumberInput';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { memo, useCallback, useMemo } from 'react';
import { StyleProp, StyleSheet, TextStyle, ViewStyle } from 'react-native';

export const Stepper = memo(
  ({
    editable = true,
    value,
    min,
    max = Number.POSITIVE_INFINITY,
    format,
    buttonIconSize,
    style,
    buttonStyle,
    inputStyle,
    readonlyTextStyle,
    onChange,
  }: {
    editable?: boolean;
    value: number;
    min: number;
    max?: number;
    format?: (value: number) => string;
    style?: StyleProp<ViewStyle>;
    inputStyle?: StyleProp<TextStyle>;
    readonlyTextStyle?: StyleProp<TextStyle>;
    buttonStyle?: StyleProp<ViewStyle>;
    buttonIconSize?: number;
    onChange?: (value: number) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const styles = useStyles();

    const onMinusPress = useCallback(() => {
      const updated = Math.max(min, value - 1);
      if (updated !== value) {
        onChange?.(updated);
      }
    }, [min, onChange, value]);

    const onPlusPress = useCallback(() => {
      const updated = Math.min(max, value + 1);
      if (updated !== value) {
        onChange?.(updated);
      }
    }, [max, onChange, value]);

    const onInputChange = useCallback(
      (v: number | undefined) => {
        const updated = Math.max(min, Math.min(max, v ?? min));
        if (updated !== value) {
          onChange?.(updated);
        }
      },
      [max, min, onChange, value],
    );

    if (!editable) {
      return (
        <Typography.Text style={[styles.readOnlyText, readonlyTextStyle]}>
          {format?.(value) ?? value}
        </Typography.Text>
      );
    }

    return (
      <HStack justifyContent="flex-start" style={style}>
        <Center.Touchable
          style={[styles.button, buttonStyle]}
          onPress={onMinusPress}
        >
          <Icon name="stepper-minus" size={buttonIconSize ?? rpx(16)} />
        </Center.Touchable>
        <NumberInput
          value={value}
          numberOfLines={1}
          onChangeValue={onInputChange}
          style={[styles.input, inputStyle]}
        />
        <Center.Touchable
          style={[styles.button, buttonStyle]}
          onPress={onPlusPress}
        >
          <Icon name="stepper-plus" size={buttonIconSize ?? rpx(16)} />
        </Center.Touchable>
      </HStack>
    );
  },
);

const useStyles = () => {
  const { rpx } = useResponsiveContext();
  return useMemo(
    () =>
      StyleSheet.create({
        button: {
          width: rpx(20),
          height: rpx(16),
        },
        input: {
          fontSize: rpx(10),
          fontWeight: 'bold',
          height: rpx(16),
          color: '#333',
          backgroundColor: '#eee',
          minWidth: rpx(20),
          textAlign: 'center',
          borderRadius: rpx(3),
          paddingHorizontal: 0,
          paddingTop: 0,
          paddingBottom: 0,
          textAlignVertical: 'center',
        },
        readOnlyText: {},
      }),
    [rpx],
  );
};
